// ============================================================
// Viashift — Session store (Phase 1, Epic 1B.4)
// ============================================================
// Global Zustand store holding the authenticated user's session
// (identity + resolved clinic membership). Supersedes the
// React Context implementation from 1B.3 — same SessionValue
// shape, new propagation mechanism.
//
// Why Zustand instead of Context:
//
//   - Components outside the AuthenticatedLayout subtree can
//     (eventually) read without being remounted under a new
//     provider. Immediately useful for things like a top-level
//     toast system or analytics hook.
//
//   - Imperative writes from non-React code paths (auth state
//     change listener, fetch callbacks) don't need a dispatcher
//     plumbed through props or a ref.
//
//   - Selectors avoid re-rendering consumers when unrelated
//     fields change — relevant once more state lands in the
//     store (ticket 1B.5 role gating, later switcher UI).
//
// Contract (unchanged from 1B.3):
//
//   - useSession() throws when no session is set. Only call from
//     inside the authenticated branch of AuthenticatedLayout.
//     Components rendered by the public layout must not call it.
//
//   - The store is populated by AuthenticatedLayout after clinic
//     resolution succeeds, and cleared on auth loss / fetch
//     failure. AuthenticatedLayout is the sole writer today;
//     later tickets may introduce setActiveClinic (switcher) or
//     refresh actions.
//
// 3D.1 addition:
//
//   - SessionValue.permissions — ReadonlySet of permission names
//     the user holds for the active clinic. Computed from
//     defaultPermissionsForRole(activeMembership.role) at
//     session-set time. Read sync via hasPermission(session, name)
//     from src/lib/permissions. When 7D's permission_grants
//     table lands, this populates from a one-time fetch at
//     session load instead of from defaults; the cache contract
//     (ReadonlySet<PermissionName>) does not change.
//
// 3D.2 addition:
//
//   - SessionValue.viewAsRole — owner-only demo impersonation.
//     When non-null, SessionValue.permissions reflects the
//     impersonated role's defaults rather than the real role's.
//     Server-side action gates are unaffected — Edge Functions
//     re-derive permissions from the real user's clinic_members
//     row via has_permission().
//
//   - setViewAsRole(role) — mutates the store atomically: updates
//     viewAsRole, recomputes permissions, persists to sessionStorage.
//     Passing the user's real role normalizes to null (clears).
//     Validates strict-below-real-role; invalid targets are
//     no-ops with a console.warn.
//
//   - VIEW_AS_STORAGE_KEY / readPersistedViewAsRole — exported
//     for App.tsx's session-load hydration. sessionStorage
//     persists viewAsRole within a single browser session;
//     closing the tab clears it.
// ============================================================

import { create } from 'zustand';

import type { PermissionName } from '@/lib/permissions/catalog';
import { defaultPermissionsForRole } from '@/lib/permissions/catalog';
import { ROLE_HIERARCHY } from '@/lib/roles';

export type ClinicRole = 'owner' | 'manager' | 'staff';
export type ClinicProfile = 'omfs' | 'aesthetic' | 'medspa';

export interface Membership {
  clinic_id: string;
  clinic_name: string;
  clinic_profile: ClinicProfile;
  role: ClinicRole;
  /** ISO timestamp; memberships are sorted ASC for the "first by date" default. */
  created_at: string;
}

export interface SessionValue {
  userId: string;
  email: string;
  /** All memberships for the signed-in user, ordered oldest-first. */
  memberships: Membership[];
  /** Currently selected clinic. First by created_at until the switcher lands. */
  activeMembership: Membership;
  /** Permission names the user holds for the active clinic. Populated
   *  at session-set time in App.tsx via defaultPermissionsForRole().
   *  Reflects viewAsRole impersonation when non-null (3D.2). */
  permissions: ReadonlySet<PermissionName>;
  /** Owner-only demo impersonation target. Null when not impersonating.
   *  When set, `permissions` reflects defaults for this role rather
   *  than activeMembership.role. Server-side action gates are
   *  unaffected — Edge Functions re-derive from the real user. */
  viewAsRole: ClinicRole | null;
}

interface SessionStoreState {
  session: SessionValue | null;
}

// The extra `()` after the type parameter is required by Zustand v5
// for explicit generics — without it TS can't resolve the overloads.
export const useSessionStore = create<SessionStoreState>()(() => ({
  session: null,
}));

/**
 * Read the active session. Throws when no session is set — this is a
 * programmer error (component rendered outside the authenticated
 * branch), not a user-facing state. Public-layout components must
 * not call this.
 *
 * Components that need the session only when it happens to be set
 * (e.g. a header variant that renders for both public and
 * authenticated surfaces) should read directly:
 *
 *   const session = useSessionStore((s) => s.session);
 *   if (!session) return <PublicVariant />;
 */
export function useSession(): SessionValue {
  const session = useSessionStore((s) => s.session);
  if (!session) {
    throw new Error(
      'useSession() called with no active session. ' +
        'Only components rendered inside AuthenticatedLayout may call it.',
    );
  }
  return session;
}

// ---- Narrowing helpers ---------------------------------------------
//
// Supabase's generated types express role/profile as `string` because
// CHECK constraints don't propagate to TypeScript. We narrow on read
// so downstream consumers can rely on the union. A row that fails
// either check is dropped at mapping time with a console.error — this
// should never happen in production but is worth surfacing in dev.

export function isClinicRole(value: string): value is ClinicRole {
  return value === 'owner' || value === 'manager' || value === 'staff';
}

export function isClinicProfile(value: string): value is ClinicProfile {
  return value === 'omfs' || value === 'aesthetic' || value === 'medspa';
}

// ============================================================
// 3D.2 — viewAsRole impersonation
// ============================================================

/** sessionStorage key. Scoped per-browser-session — closing the
 *  tab clears it. Survives page refreshes during a single
 *  browsing session, which is the demo / dev-velocity sweet
 *  spot. localStorage would be too sticky (forgotten state
 *  surfaces next week); in-memory would be too transient
 *  (Vite HMR / manual refresh resets every time). */
export const VIEW_AS_STORAGE_KEY = 'viashift-view-as-role';

/**
 * True iff `candidate` is strictly below `realRole` in the role
 * hierarchy. Owner can impersonate manager + staff; manager can
 * impersonate staff (when the view-as permission is extended);
 * staff has no valid targets. Selecting one's real role is
 * caller-normalized to null (clearing) rather than being a
 * valid impersonation target.
 */
export function isImpersonationTarget(
  realRole: ClinicRole,
  candidate: ClinicRole,
): boolean {
  return ROLE_HIERARCHY[candidate] < ROLE_HIERARCHY[realRole];
}

/**
 * Read and validate a persisted viewAsRole from sessionStorage.
 * Returns the role if storage contains a valid strict-below
 * target for `realRole`, otherwise null. Clears the storage
 * entry on invalid values so a future load doesn't keep
 * surfacing a stale impersonation that no longer makes sense
 * (e.g. user's role changed, or different clinic context).
 */
export function readPersistedViewAsRole(
  realRole: ClinicRole,
): ClinicRole | null {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // sessionStorage may be unavailable in some embedded /
    // private-mode contexts. Treat as "no persisted value."
    return null;
  }
  if (stored === null) return null;
  if (!isClinicRole(stored)) {
    clearPersistedViewAsRole();
    return null;
  }
  if (!isImpersonationTarget(realRole, stored)) {
    clearPersistedViewAsRole();
    return null;
  }
  return stored;
}

function clearPersistedViewAsRole(): void {
  try {
    sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // Same defensive posture as readPersistedViewAsRole — if
    // storage is unavailable, removal is a no-op.
  }
}

function writePersistedViewAsRole(role: ClinicRole): void {
  try {
    sessionStorage.setItem(VIEW_AS_STORAGE_KEY, role);
  } catch {
    // If storage is unavailable, the impersonation still works
    // for the lifetime of the current page (in-memory store
    // state), it just doesn't survive a refresh. Acceptable
    // degradation — log loudly so we notice in dev.
    console.warn(
      '[viashift] sessionStorage unavailable; viewAsRole will not persist across page refreshes.',
    );
  }
}

/**
 * Set or clear the viewAsRole impersonation. Atomically updates
 * SessionValue.viewAsRole + SessionValue.permissions and
 * persists the value to sessionStorage.
 *
 * Caller contract:
 *   - Pass null (or the user's real role) to clear impersonation.
 *   - Pass a strict-below role to enter impersonation.
 *   - Invalid values are no-ops with a console.warn — defense
 *     in depth; the UI should never pass an invalid value.
 *
 * No-op when no session is active.
 */
export function setViewAsRole(role: ClinicRole | null): void {
  useSessionStore.setState((state) => {
    if (!state.session) return state;

    const realRole = state.session.activeMembership.role;

    // Normalize: selecting your own real role clears the
    // impersonation rather than entering a "view as your real
    // role" state (which would be a no-op semantically but
    // would still display the demo banner — confusing).
    const normalized: ClinicRole | null =
      role === null || role === realRole ? null : role;

    if (normalized !== null && !isImpersonationTarget(realRole, normalized)) {
      console.warn(
        '[viashift] setViewAsRole called with invalid target',
        { realRole, candidate: normalized },
      );
      return state;
    }

    if (normalized === null) {
      clearPersistedViewAsRole();
    } else {
      writePersistedViewAsRole(normalized);
    }

    const effectiveRole = normalized ?? realRole;
    return {
      session: {
        ...state.session,
        viewAsRole: normalized,
        permissions: defaultPermissionsForRole(effectiveRole),
      },
    };
  });
}
